// Internal module — import from `../CommunicationStyleService` instead.
//
// StyleProfileService (merged from StyleProfileService.js - Sprint 3 #51)
// Profile CRUD for saved writing-style profiles. Kept as a standalone class
// rather than methods on CommunicationStyleService so the existing consumer
// API (new StyleProfileService(), default singleton) is preserved verbatim.

import { generateClient } from 'aws-amplify/data';
import { StyleProfile } from '../../models/StyleProfile';

export class StyleProfileService {
  constructor(client = null) {
    this.client = client || generateClient();
    this.maxProfiles = 10;
    this.cache = null;
    this.cacheTime = null;
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
    this.activeProfile = null;
  }

  // CRUD Operations
  async createProfile(profileData, userId) {
    const profile = new StyleProfile(profileData); // validates

    const existingProfiles = await this.getUserProfiles(userId);
    if (existingProfiles.length >= this.maxProfiles) {
      throw new Error(`Maximum profile limit reached (${this.maxProfiles})`);
    }

    const { data } = await this.client.models.StyleProfile.create({
      ...profileData,
      userId,
      isPreset: false
    });

    this.clearCache();
    return data;
  }

  async getUserProfiles(userId) {
    if (this.cache && this.cacheTime && (Date.now() - this.cacheTime) < this.cacheDuration) {
      return this.cache;
    }

    const result = await this.client.models.StyleProfile.list({
      filter: { userId: { eq: userId } }
    });

    this.cache = result?.data || [];
    this.cacheTime = Date.now();
    return this.cache;
  }

  async getProfile(profileId) {
    const { data } = await this.client.models.StyleProfile.get({ id: profileId });
    return data;
  }

  async updateProfile(profileId, updates) {
    if (updates.writingStyle || updates.tone || updates.complexity) {
      new StyleProfile({ name: 'test', ...updates });
    }

    const { data } = await this.client.models.StyleProfile.update({ id: profileId, ...updates });
    this.clearCache();
    return data;
  }

  async deleteProfile(profileId) {
    await this.client.models.StyleProfile.delete({ id: profileId });
    this.clearCache();
    return true;
  }

  // Preset Profiles
  getPresetProfiles() {
    return StyleProfile.getPresets();
  }

  async getAllProfiles(userId) {
    const userProfiles = await this.getUserProfiles(userId);
    return [...this.getPresetProfiles(), ...userProfiles];
  }

  async duplicatePreset(presetName, userId) {
    const presets = this.getPresetProfiles();
    const preset = presets.find(p => p.name === presetName);
    if (!preset) throw new Error(`Preset "${presetName}" not found`);

    const duplicatedData = { ...preset.toJSON(), name: `${preset.name} (Copy)`, isPreset: false, userId };
    delete duplicatedData.id;
    delete duplicatedData.createdAt;
    delete duplicatedData.updatedAt;

    return await this.createProfile(duplicatedData, userId);
  }

  // Profile Selection
  setActiveProfile(profile) { this.activeProfile = profile; }
  getActiveProfile() { return this.activeProfile; }
  clearActiveProfile() { this.activeProfile = null; }
  getDefaultProfile() { return this.getPresetProfiles()[0]; }

  // Export/Import
  exportProfile(profile) {
    const exported = { ...profile.toJSON ? profile.toJSON() : profile };
    delete exported.id;
    delete exported.userId;
    delete exported.createdAt;
    delete exported.updatedAt;
    delete exported.isPreset;
    return exported;
  }

  async importProfile(jsonData, userId) {
    return await this.createProfile({ ...jsonData, userId, isPreset: false }, userId);
  }

  // Cache Management
  clearCache() {
    this.cache = null;
    this.cacheTime = null;
  }

  // Apply Style to Content
  async applyStyleToContent(content, profileId = null) {
    let profile = this.activeProfile;
    if (profileId) profile = await this.getProfile(profileId);
    if (!profile) profile = this.getDefaultProfile();

    const systemPrompt = profile.generateSystemPrompt
      ? profile.generateSystemPrompt()
      : new StyleProfile(profile).generateSystemPrompt();

    const instructions = profile.getStyleInstructions
      ? profile.getStyleInstructions()
      : new StyleProfile(profile).getStyleInstructions();

    return { originalContent: content, systemPrompt, styleInstructions: instructions, profile };
  }
}

// Singleton instance for the style profile service
export const styleProfileServiceInstance = new StyleProfileService();
